import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import * as api from './tenderOpeningApi';
import { TO_STATUS, isViewable } from './tenderOpeningConstants';
import { formatDateTime } from './tenderOpeningFormatters';

// Crude Tender Opening list — wired to GET /v1/TenderOpening (crudeApi).
//
// The business stage after Procurement: a Submitted ProcurementValidity advances
// here (its Submit creates the TenderOpening row). The server only returns
// Procurements that already have a TenderOpening row, so no client-side filter is
// needed. The list `id` is TenderOpening.Id.
//
// Phase 1 = list only. The Edit and Detail pages are a later phase; the Action
// buttons follow the status rule now and will be wired to
// /crude/tender-opening/edit/:id and /dtl/:id once those pages exist.

// UI column sortKey -> PascalCase, whitelisted API sortField (contract section 1).
const SORT_FIELD_MAP = {
  procId: 'ProcurementCode',
  title: 'Title',
  procurementType: 'JenisPengadaanName',
  procurementMethod: 'MekanismePengadaanName',
  description: 'Description',
  requestHps: 'RequestHpsStepStatus',
  inviteDmutDocument: 'InviteDmutStepStatus',
  invitePanitiaTender: 'InvitePanitiaTenderStepStatus',
  reqFreightAvailability: 'RequestFreightAvailabilityStepStatus',
  createdDate: 'Created',
  updatedDate: 'Modified',
  mainStatus: 'PhaseName',
  toStatus: 'PhaseStatusName',
};

// Header putih di atas navy — mengikuti gaya header tabel Planning/Procurement.
const headerLabel = label => (
  <span
    className='text-uppercase fw-semibold fs-13 mb-0 text-white'
    style={{ whiteSpace: 'normal', lineHeight: 1.2, wordBreak: 'break-word' }}
  >
    {label}
  </span>
);

// 3 kolom checklist mengikuti desain acuan (Freight menempel di tab HPS, tidak
// lagi jadi kolom sendiri). Urutan = urutan step di halaman detail.
const LIST_CHECK_COLUMNS = [
  { key: 'requestHps', label: 'Request HPS' },
  { key: 'inviteDmutDocument', label: 'Invite DMUT' },
  { key: 'invitePanitiaTender', label: 'Invite Panitia Tender' },
];

// Dot checklist (desain acuan): hijau centang = selesai, kuning = step aktif
// (step belum-selesai pertama), abu = belum tersentuh.
const StepDot = ({ state, label }) => {
  if (state === 'done') {
    return (
      <span
        className='d-inline-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success border border-success'
        style={{ width: 20, height: 20 }}
        title={`${label}: Completed`}
        aria-label={`${label} completed`}
      >
        <i className='ri-check-line fs-12'></i>
      </span>
    );
  }
  return (
    <span
      className={`d-inline-block rounded-circle ${
        state === 'current' ? 'bg-warning' : 'bg-light border'
      }`}
      style={{ width: 16, height: 16 }}
      title={`${label}: ${state === 'current' ? 'Current step' : 'Not completed'}`}
      aria-label={`${label} ${state === 'current' ? 'current step' : 'not completed'}`}
    ></span>
  );
};

// Status per step: 'done' | 'current' (step belum-selesai pertama) | 'pending'.
const stepState = (checks = {}, key) => {
  if (checks[key]) return 'done';
  const firstPending = LIST_CHECK_COLUMNS.find(c => !checks[c.key]);
  return firstPending?.key === key ? 'current' : 'pending';
};

// Status list turunan (desain acuan): Submitted (3 step selesai) / On Progress;
// status recall dari server tetap ditampilkan apa adanya.
const deriveListStatus = row => {
  if (
    row.toStatus === TO_STATUS.SOFT_RECALL ||
    row.toStatus === TO_STATUS.HARD_RECALL
  ) {
    return row.toStatus;
  }
  const allDone = LIST_CHECK_COLUMNS.every(c => Boolean(row.checks?.[c.key]));
  return allDone ? 'Submitted' : 'On Progress';
};

const STATUS_PILL_STYLE = {
  'On Progress': { backgroundColor: '#f59e0b' },
  Submitted: { backgroundColor: '#0b5345' },
  [TO_STATUS.SOFT_RECALL]: { backgroundColor: '#dc3545' },
  [TO_STATUS.HARD_RECALL]: { backgroundColor: '#dc3545' },
};

const StatusPill = ({ row }) => {
  const status = deriveListStatus(row);
  return (
    <span
      className='badge rounded-pill text-white px-3 py-1'
      style={STATUS_PILL_STYLE[status] || { backgroundColor: '#6c757d' }}
    >
      {status}
    </span>
  );
};

const TenderOpeningListView = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // Status-breakdown for the summary cards (whole set, not the current filter).
  const [summary, setSummary] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    done: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  // Filter Status (desain acuan) — client-side pada halaman yang tampil.
  const [statusFilter, setStatusFilter] = useState('');

  document.title = 'Tender Opening';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortField, sortDirection]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { items: rows, totalCount } = await api.listTenderOpening({
        page: currentPage,
        pageSize: rowsPerPage,
        searchText: debouncedSearch,
        sortField: sortField ? SORT_FIELD_MAP[sortField] : null,
        sortOrder: sortDirection === 'desc' ? 'Desc' : 'Asc',
      });
      setItems(rows);
      setTotalRows(totalCount);
    } catch (error) {
      toast.error(`Failed to load tender opening list: ${error}`);
      setItems([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, rowsPerPage, sortField, sortDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Summary cards reflect the whole set, so load once (and silently — a failure
  // here just leaves the cards at 0 rather than blocking the list).
  useEffect(() => {
    let active = true;
    api
      .summaryTenderOpening()
      .then(s => {
        if (active) setSummary(s);
      })
      .catch(() => {
        if (active) setSummary({ total: 0, open: 0, inProgress: 0, done: 0 });
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSort = (column, direction) => {
    setSortField(column.sortKey || null);
    setSortDirection(direction);
  };

  // Action rule (rule-tender-opening.md §List + contract §1): Edit while
  // Open / In Progress / On Soft Recall; View Detail once Done or On Hard Recall.
  const handleEdit = row => navigate(`/crude/tender-opening/edit/${row.id}`);
  const handleView = row => navigate(`/crude/tender-opening/dtl/${row.id}`);

  const columns = [
    {
      name: headerLabel('Id Procurement'),
      sortKey: 'procId',
      sortable: true,
      wrap: true,
      width: '180px',
      selector: row => row.procId,
      cell: row => <span className='fw-semibold'>{row.procId}</span>,
    },
    {
      name: headerLabel('Title'),
      sortKey: 'title',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '220px',
      selector: row => row.title,
    },
    {
      name: headerLabel('Procurement Type'),
      sortKey: 'procurementType',
      sortable: true,
      wrap: true,
      minWidth: '200px',
      selector: row => row.procurementType,
    },
    {
      name: headerLabel('Procurement Method'),
      sortKey: 'procurementMethod',
      sortable: true,
      wrap: true,
      minWidth: '170px',
      selector: row => row.procurementMethod,
    },
    {
      name: headerLabel('Description'),
      sortKey: 'description',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '240px',
      selector: row => row.description,
    },
    // 3 kolom checklist (desain acuan) — dot abu/kuning/hijau per step.
    ...LIST_CHECK_COLUMNS.map(step => ({
      name: headerLabel(step.label),
      sortKey: step.key,
      sortable: true,
      center: true,
      width: '140px',
      selector: row => Boolean(row.checks?.[step.key]),
      cell: row => (
        <StepDot state={stepState(row.checks, step.key)} label={step.label} />
      ),
    })),
    {
      name: headerLabel('Created Date'),
      sortKey: 'createdDate',
      sortable: true,
      wrap: true,
      width: '160px',
      selector: row => row.createdDate,
      cell: row => formatDateTime(row.createdDate),
    },
    {
      name: headerLabel('Updated Date'),
      sortKey: 'updatedDate',
      sortable: true,
      wrap: true,
      width: '160px',
      selector: row => row.updatedDate,
      cell: row => formatDateTime(row.updatedDate),
    },
    // Satu kolom Status turunan (desain acuan) menggantikan Main Status +
    // Tender Opening Status: Submitted saat 3 step selesai, selain itu
    // On Progress; status Recall dari server tampil apa adanya.
    {
      name: headerLabel('Status'),
      sortKey: 'toStatus',
      sortable: true,
      center: true,
      width: '160px',
      selector: row => row.toStatus,
      cell: row => <StatusPill row={row} />,
    },
    {
      name: headerLabel('Action'),
      width: '110px',
      center: true,
      // Status-driven (rule-tender-opening.md §List): View Detail for Done /
      // On Hard Recall; Edit otherwise (Open / In Progress / On Soft Recall).
      cell: row =>
        isViewable(row.toStatus) ? (
          <button
            className='btn btn-soft-secondary btn-sm btn-icon'
            title='View Detail'
            onClick={() => handleView(row)}
          >
            <i className='ri-eye-line align-bottom'></i>
          </button>
        ) : (
          <button
            className='btn btn-soft-primary btn-sm btn-icon'
            title='Edit'
            onClick={() => handleEdit(row)}
          >
            <i className='ri-pencil-line align-bottom'></i>
          </button>
        ),
    },
  ];

  // Summary cards — counts default to 0 when the status has no data.
  const statusCards = [
    {
      label: 'Total Tender Opening',
      value: summary.total || 0,
      icon: 'ri-stack-line',
      color: 'primary',
    },
    {
      label: TO_STATUS.OPEN,
      value: summary.open || 0,
      icon: 'ri-folder-open-line',
      color: 'secondary',
    },
    {
      label: TO_STATUS.IN_PROGRESS,
      value: summary.inProgress || 0,
      icon: 'ri-loader-2-line',
      color: 'warning',
    },
    {
      label: TO_STATUS.DONE,
      value: summary.done || 0,
      icon: 'ri-checkbox-circle-line',
      color: 'success',
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Tender Opening' pageTitle='CRUDE' />

          {/* ---- Summary cards: per Tender Opening Status ---- */}
          <Row className='g-3 mb-1'>
            {statusCards.map(c => (
              <Col xl={3} md={6} key={c.label}>
                <Card
                  className={`card-animate border shadow-none bg-soft-${c.color}`}
                >
                  <CardBody className='py-3'>
                    <div className='d-flex align-items-center'>
                      <div className='flex-shrink-0 me-3'>
                        <i className={`${c.icon} fs-1 text-${c.color}`}></i>
                      </div>
                      <div>
                        <p className='text-muted mb-1 fw-semibold text-uppercase fs-12'>
                          {c.label}
                        </p>
                        <h4 className='mb-0'>{c.value}</h4>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={12}>
              <Card id='TenderOpeningList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Tender Opening List
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap align-items-center'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search Title / Id / Description...'
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                          />
                        </div>
                        <Input
                          type='select'
                          className='form-select form-select-sm w-auto'
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                        >
                          <option value=''>Semua Status</option>
                          <option value='On Progress'>On Progress</option>
                          <option value='Submitted'>Submitted</option>
                        </Input>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div className='table-responsive'>
                    <DataTable
                      columns={columns}
                      data={
                        statusFilter
                          ? items.filter(
                              row => deriveListStatus(row) === statusFilter
                            )
                          : items
                      }
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={page => setCurrentPage(page)}
                      onChangeRowsPerPage={newPerPage => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      progressPending={loading}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No tender opening found
                        </div>
                      }
                      customStyles={{
                        headRow: {
                          style: { minHeight: '56px' },
                        },
                        headCells: {
                          style: {
                            // Navy gelap + teks putih mengikuti Planning/
                            // Procurement (desain acuan).
                            backgroundColor: '#1e3a56',
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid rgba(255,255,255,0.15)',
                            // Let long labels wrap onto multiple lines instead of
                            // clipping inside the fixed-width columns.
                            whiteSpace: 'normal',
                            alignItems: 'flex-start',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: { style: { minHeight: '50px' } },
                      }}
                    />
                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TenderOpeningListView;
